// PostHog for calendar.lyhevents.com.
//
// NOTE: this is its OWN PostHog project, deliberately not the Hangar client key that
// lives in hangar-indy/observability.template.ts. Client analytics and Andrew's own
// projects must not share a project or the data mixes.
//
// The key is read from the environment rather than hardcoded so the repo can be public
// without pinning a project id into source. A PostHog *browser* key is public-safe by
// design (it can only write events), but env keeps it swappable per environment.
//
//   .env.local          VITE_POSTHOG_KEY=phc_...
//                       VITE_POSTHOG_HOST=https://us.i.posthog.com   (optional)
//   Netlify             same two vars, or production gets no analytics.
//
// With no key set, every function here is a no-op. That's intentional: a missing key
// should degrade to "no analytics", never to a crash or a console full of errors.

import type { PostHog } from 'posthog-js'

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  'https://us.i.posthog.com'

let enabled = false

// ---- Loading posthog-js after first paint, not in the first bundle -------------
//
// Until 2026-09-23 posthog-js was a static import, so its chunk (73 KB gzip) sat
// in the page's modulepreload list and was downloaded and parsed before the
// calendar could paint. Lighthouse that day counted about 38 KB of it as unused
// on first load: nothing a visitor sees needs analytics to render. It now
// arrives through a dynamic import() that only starts once the page has loaded.
//
// Andrew accepted the cost explicitly: a visitor who leaves in the first second
// or two may never be counted, and an error thrown in that window may not be
// captured, because PostHog is not there yet to see either.
//
// Every export keeps working before PostHog arrives. Each call is pushed onto
// `pending` as a function of the PostHog instance and replayed in the order it
// was made once init() has run. `ph` is null until then.
let ph: PostHog | null = null
let pending: Array<(p: PostHog) => void> = []

function withPosthog(op: (p: PostHog) => void) {
  if (!enabled) return
  if (ph) op(ph)
  else pending.push(op)
}

/**
 * Start the import after first paint, in three steps:
 *
 * 1. The window `load` event: the app's own chunks, CSS and fonts are done.
 * 2. The first contentful paint. `load` alone is not enough, and neither is the
 *    frame after it: this page is one React root whose first content waits on
 *    the Supabase event query. In a headless run on 2026-09-23, `load` fired at
 *    35 ms and the next frame painted only the empty shell, so the import started
 *    at 166 ms while first contentful paint came at 200 ms. The browser's own
 *    `first-contentful-paint` entry is the reliable signal (Chrome, Firefox and
 *    Safari 14.1+). A tab opened in the background records no paint until it is
 *    shown, so a 5 s timer after `load` starts the import anyway.
 * 3. An idle moment, so the download does not compete with the renders that
 *    follow. The 3 s timeout caps that wait on a busy phone, and Safari, which
 *    has no requestIdleCallback, goes straight on.
 */
function loadAfterFirstPaint(start: () => void) {
  let started = false
  const whenIdle = () => {
    if (started) return
    started = true
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 3000 })
    else start()
  }
  const afterFirstPaint = () => {
    const fallback = setTimeout(whenIdle, 5000)
    try {
      const observer = new PerformanceObserver((list) => {
        if (!list.getEntriesByName('first-contentful-paint').length) return
        observer.disconnect()
        clearTimeout(fallback)
        whenIdle()
      })
      // buffered: an FCP that already happened before `load` is delivered too.
      observer.observe({ type: 'paint', buffered: true })
    } catch {
      // No paint timing in this browser: the 5 s timer above covers it.
    }
  }
  if (document.readyState === 'complete') afterFirstPaint()
  else window.addEventListener('load', afterFirstPaint, { once: true })
}

// ---- Marking Andrew's own browsers so they drop out of the numbers --------------
//
// PostHog's "Filter out internal and test users" setting used to rely only
// on a hand-kept list of $device_id values plus the home IP prefix. Both break on
// iOS: a website can read no hardware id at all, only one it stored itself, and
// WebKit's tracking prevention (on by default in every iOS browser since iOS 14)
// throws that storage away after 7 days without a visit. So an iPhone kept minting
// fresh ids that were never on the list, and on cellular, where the home IP rule
// does not reach, every one of its visits counted as a real visitor. Measured
// 2026-09-12: 72 events leaked that way in 30 days from one phone.
//
// The fix tags the BROWSER rather than listing it. `internal_user` is registered as
// a super property, so posthog-js sends it on every event from this browser on any
// network, and the project's test filter carries `internal_user` is not set. Chosen
// over posthog.setInternalOrTestUser(), which sets a PERSON property instead: that
// forces a person profile for an anonymous browser and needs a cohort PostHog only
// recomputes on a delay, where an event property takes effect on the next event.
//
// Two ways a browser gets tagged: signing in as the admin does it automatically (see
// auth.tsx), and visiting any page with ?internal=1 does it without signing in.
// ?internal=0 undoes it. The flag is ALSO kept under our own key, so it can be put
// back if posthog-js's own storage is ever reset.
const INTERNAL_KEY = 'lyh_internal'

function internalFlagSet(): boolean {
  try {
    return localStorage.getItem(INTERNAL_KEY) === '1'
  } catch {
    return false // storage blocked -> treat as an ordinary visitor
  }
}

/** Tag this browser as Andrew's, so PostHog's internal filter drops its events. */
export function markInternal() {
  try {
    localStorage.setItem(INTERNAL_KEY, '1')
  } catch {
    // storage blocked: the super property below still covers this page load
  }
  withPosthog((p) => p.register({ internal_user: true }))
}

// Set when ?internal=0 is read before PostHog has loaded, so `loaded` can drop
// the tag before the first $pageview (see there).
let unmarkedBeforeLoad = false

function unmarkInternal() {
  if (!ph) unmarkedBeforeLoad = true
  try {
    localStorage.removeItem(INTERNAL_KEY)
  } catch {
    // nothing stored, nothing to remove
  }
  withPosthog((p) => p.unregister('internal_user'))
}

/**
 * Read ?internal=1 / ?internal=0, act on it, then REMOVE it from the address bar.
 * Leaving it there would be a quiet leak in the other direction: a link copied from
 * the address bar and pasted into a Facebook reply would tag every real visitor who
 * clicked it as internal, and they would vanish from the numbers. Every other query
 * param is left exactly as it was, per the rule in urlFilters.ts.
 */
function applyInternalParam() {
  try {
    const url = new URL(window.location.href)
    const v = url.searchParams.get('internal')
    if (v === null) return
    if (v === '0') unmarkInternal()
    else markInternal()
    url.searchParams.delete('internal')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
  } catch {
    // malformed URL or blocked history API: leave the page as it is
  }
}

export function initAnalytics() {
  if (!KEY) return // no key configured -> stay silent
  const key = KEY
  enabled = true
  // Runs now, not when PostHog arrives, so the param is off the address bar
  // before the visitor can copy the link. It only touches our own localStorage
  // key; the PostHog side of it waits in `pending` like everything else, and a
  // ?internal=1 on the very first page is also picked up by `loaded` below,
  // which reads that key before the first $pageview goes out.
  applyInternalParam()
  loadAfterFirstPaint(() => {
    import('posthog-js')
      .then(({ default: posthog }) => startPosthog(posthog, key))
      .catch(() => {
        // Chunk failed to download (offline, a deploy swapped the hash out from
        // under an old tab). Analytics goes quiet for this visit rather than
        // holding every call in memory forever.
        enabled = false
        pending = []
      })
  })
}

function startPosthog(posthog: PostHog, key: string) {
  const isProd = import.meta.env.PROD

  posthog.init(key, {
    api_host: HOST,
    // This is a public calendar; almost every visitor is anonymous and will never log
    // in. 'identified_only' avoids creating a person profile for every drive-by
    // visitor, which is the setting that keeps this affordable at consumer traffic.
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    // Catch errors we did NOT hand-instrument. Without this we only ever see a
    // failure somebody remembered to wrap in track(), which is how a subscribe
    // dying on a thrown fetch surfaced as a bare "network" with the error itself
    // discarded. Chosen over Sentry deliberately: no second vendor, no extra SDK
    // in the bundle, and exceptions land beside the product events you read them
    // against. Still respects the opt-out below, so dev errors stay out.
    capture_exceptions: true,
    // Off because there is nothing to show. PageSpeed (2026-09-23) caught
    // posthog-js fetching surveys.js (34 KB) on every first visit, and the
    // project has no surveys at all (checked the same day: system.surveys is
    // empty and the Surveys product was never enabled). If you ever create a
    // survey for the calendar, delete this line first or it will never appear.
    //
    // Deliberately NOT trimmed: session recording (Andrew watches replays, and
    // posthog-recorder.js loads lazily from remote config anyway), exception
    // capture above, dead-click autocapture (feeds the heatmaps product, which
    // is on) and web vitals. Those are features in use, not dead weight.
    disable_surveys: true,
    // Replays show typed text in plain letters (Andrew, 2026-09-25): what someone
    // types into the search box is the whole point of watching a search replay,
    // and posthog-js masks every input to asterisks by default. Email and
    // password fields stay masked, because the subscribe and submit forms take a
    // visitor's email address and a replay has no reason to keep it.
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true, email: true },
    },
    loaded: (ph) => {
      // Local dev shouldn't pollute the numbers Andrew reads.
      if (!isProd) ph.opt_out_capturing()
      // `loaded` runs BEFORE posthog-js sends the automatic first $pageview (it
      // schedules that on a timeout after this callback returns), so tagging here
      // covers the very first event of the visit, not just the ones after it.
      if (internalFlagSet()) ph.register({ internal_user: true })
      // The same for ?internal=0. The queued unregister only replays after that
      // first $pageview (see the replay timer below), and posthog-js still holds
      // the tag from earlier visits, so without this the page that untags the
      // browser would itself go out tagged. Before 2026-09-23 the unregister ran
      // straight after init() and this never came up.
      else if (unmarkedBeforeLoad) ph.unregister('internal_user')
    },
  })
  ph = posthog
  // Replay what was called while the chunk was loading. On a timer rather than
  // right here because init() sends the automatic $pageview on its own 1 ms
  // timer, and timers with the same delay run in the order they were set: this
  // one was set second, so the visit still starts with its $pageview and the
  // queued events follow in the order they happened. Each queued capture carries
  // the time it was called (see track), so the gap does not skew any timeline.
  setTimeout(() => {
    const queued = pending
    pending = []
    for (const op of queued) op(posthog)
  }, 1)
}

/** Fire a named event. Silently ignored when analytics isn't configured. */
export function track(event: string, props?: Record<string, unknown>) {
  if (!enabled) return
  if (ph) {
    ph.capture(event, props)
    return
  }
  // Queued: stamp it with when it happened, not when PostHog got around to it.
  const timestamp = new Date()
  pending.push((p) => p.capture(event, props, { timestamp }))
}

/**
 * track(), but after the next paint. Returns a cancel function for an effect's
 * cleanup.
 *
 * For impressions fired from a React effect on mount ("the modal was shown"),
 * never for clicks. posthog.capture() reads window.innerHeight/innerWidth for
 * $viewport_height and $viewport_width, and reading those while the DOM React
 * has just committed is still unlaid-out forces the browser to do the whole
 * page's layout synchronously, right there. Lighthouse caught exactly that on
 * 2026-09-23: onboarding_shown, fired from OnboardingModal's mount effect, cost
 * a 33-45 ms forced layout inside the task that paints the first-visit picker,
 * which is the LCP element (forced-reflow-insight scored 0).
 *
 * requestAnimationFrame runs just BEFORE the next paint, so on its own it would
 * land in the same spot; the setTimeout inside it moves the capture to just
 * AFTER that paint, when layout is already clean and the read costs nothing.
 * The event is about a frame later and otherwise identical. Not for clicks: a
 * click can navigate away before the timer fires, and a click handler runs
 * against a laid-out page anyway.
 */
export function trackAfterPaint(event: string, props?: Record<string, unknown>): () => void {
  if (!enabled) return () => {}
  let timer: ReturnType<typeof setTimeout> | undefined
  const frame = requestAnimationFrame(() => {
    // track() queues it if PostHog is still loading; the replay runs well after
    // this paint, so the layout read stays cheap either way.
    timer = setTimeout(() => track(event, props), 0)
  })
  return () => {
    cancelAnimationFrame(frame)
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * Stable per-browser id for pseudonymous attribution (feedback rows). Prefers
 * the PostHog distinct_id so a feedback row cross-links to that device's
 * session history; falls back to a locally minted UUID when analytics is off
 * (dev, missing key). Same person on two devices = two ids, by design.
 *
 * Since PostHog loads after first paint (2026-09-23), a call can land before it
 * has arrived. A returning browser still gets the same distinct_id then: it is
 * read straight from the storage posthog-js left on the last visit (see
 * storedDistinctId). Only a first-ever visit that submits in the first second or
 * two, before PostHog has minted an id at all, gets the local UUID instead.
 */
export function deviceId(): string | null {
  if (enabled) {
    try {
      const id = ph ? ph.get_distinct_id() : storedDistinctId()
      if (id) return id
    } catch {
      // posthog not ready -> use the local id below
    }
  }
  try {
    const STORAGE_KEY = 'lyh_device_id'
    let id = localStorage.getItem(STORAGE_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(STORAGE_KEY, id)
    }
    return id
  } catch {
    return null // storage blocked (private mode) -> row stays anonymous
  }
}

/**
 * The distinct_id posthog-js saved on an earlier visit, read without loading it.
 * posthog-js keeps its state under ph_<token>_posthog, as JSON, in localStorage
 * and (the id part) in a cookie of the same name; that is its default
 * 'localStorage+cookie' persistence, which init() above does not change. If a
 * posthog-js upgrade ever moves this, the only effect is falling back to the
 * local UUID for calls made before PostHog loads.
 */
function storedDistinctId(): string | null {
  const name = `ph_${KEY}_posthog`
  const fromJson = (raw: string | null | undefined) => {
    if (!raw) return null
    const id = (JSON.parse(raw) as { distinct_id?: unknown }).distinct_id
    return typeof id === 'string' && id ? id : null
  }
  try {
    const stored = fromJson(localStorage.getItem(name))
    if (stored) return stored
  } catch {
    // storage blocked or not JSON -> try the cookie
  }
  try {
    const cookie = document.cookie.split('; ').find((c) => c.startsWith(name + '='))
    return fromJson(cookie && decodeURIComponent(cookie.slice(name.length + 1)))
  } catch {
    return null
  }
}

export function identifyUser(user: { id: string; email?: string | null }) {
  withPosthog((p) => p.identify(user.id, user.email ? { email: user.email } : undefined))
}

export function clearUser() {
  withPosthog((p) => {
    p.reset()
    // reset() wipes every super property, including the internal tag. Put it back
    // from our own key so signing out does not quietly turn this browser into a
    // "real visitor" again.
    if (internalFlagSet()) p.register({ internal_user: true })
  })
}
